import React, { useEffect, useState } from 'react';
import { Lock, Eye, EyeOff, ShieldCheck, Mail, Instagram, Youtube, ArrowRight, Wrench } from 'lucide-react';
import { XIcon } from '../components/ui/XIcon';
import { fetchPublicMaintenance } from '../services/site';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_62e9b668-eeea-4d98-9b43-5be9e360bcc3/artifacts/3ywhr6at_40092bbc-3a82-4c76-9cfc-e41b47b35c31%20%281%29.png";

const Maintenance = () => {
  const { login, logout } = useAuth();
  const [message, setMessage] = useState("Notre équipe finalise les derniers détails pour vous offrir une meilleure expérience. Nous serons de retour très prochainement.");
  const [imageUrl, setImageUrl] = useState('');

  const [mode, setMode] = useState(null); // 'admin' | 'tester'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetchPublicMaintenance();
        if (res?.message) setMessage(res.message);
        if (res?.image_url) setImageUrl(res.image_url);
      } catch (e) {}
    };
    load();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await login(email, password, requires2FA ? totpCode : null, rememberMe);
      if (result?.requires2FA) {
        setRequires2FA(true);
        setLoading(false);
        return;
      }
      if (result?.success) {
        const rt = result?.user_type;
        const ADMIN_ROLES = ['fondateur', 'co-fondateur'];
        const TESTER_ROLES = ['membre', 'invite'];
        // Priorité admin : un admin peut entrer par les deux sections.
        const allowed = mode === 'admin' ? ADMIN_ROLES : [...TESTER_ROLES, ...ADMIN_ROLES];
        if (allowed.includes(rt)) {
          return; // accès accordé
        }
        await logout();
        setError(mode === 'admin'
          ? "Cet espace est réservé aux administrateurs."
          : "Cet espace est réservé aux testeurs/testrices.");
        return;
      }
      await logout();
      setError("Accès réservé aux comptes autorisés pendant la maintenance.");
    } catch (err) {
      setError(err.message || 'Identifiants invalides');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative min-h-screen overflow-hidden bg-[#07110e] text-slate-100 flex items-center justify-center px-6 py-16"
      data-testid="maintenance-page"
    >
      {/* Halos d'ambiance */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-48 -left-40 w-[38rem] h-[38rem] rounded-full bg-emerald-500/20 blur-[130px] animate-pulse" style={{ animationDuration: '6s' }} />
        <div className="absolute -bottom-52 -right-40 w-[34rem] h-[34rem] rounded-full bg-teal-400/10 blur-[130px] animate-pulse" style={{ animationDuration: '8s' }} />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.6) 1px, transparent 0)', backgroundSize: '28px 28px' }}
        />
      </div>

      <div className="relative z-10 w-full max-w-xl text-center">
        {/* Logo avec anneau lumineux */}
        <div className="flex justify-center mb-9">
          <div className="relative">
            <span className="absolute inset-0 rounded-full bg-emerald-400/25 blur-2xl scale-110" aria-hidden />
            <span className="absolute -inset-2 rounded-full border border-emerald-400/30 animate-ping" style={{ animationDuration: '3.5s' }} aria-hidden />
            <div className="relative bg-white rounded-full p-2 ring-2 ring-emerald-400/40 shadow-2xl shadow-emerald-500/20 w-40 h-40 sm:w-44 sm:h-44 flex items-center justify-center overflow-hidden">
              <img
                src={imageUrl || LOGO_URL}
                alt="PADAID"
                className="w-full h-full object-cover scale-110"
                data-testid="maintenance-logo"
              />
            </div>
          </div>
        </div>

        {/* Badge de statut animé */}
        <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-1.5 text-xs font-medium text-emerald-300 mb-6">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
          </span>
          Maintenance en cours
        </div>

        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
          Nous revenons <span className="text-emerald-400">très bientôt</span>
        </h1>
        <p className="text-base text-slate-300/90 leading-relaxed max-w-md mx-auto mb-10" data-testid="maintenance-message">
          {message}
        </p>

        {/* Réassurance */}
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-400 mb-10">
          <span className="inline-flex items-center gap-2"><Wrench className="h-4 w-4 text-emerald-400/80" /> Améliorations en cours</span>
          <span className="hidden sm:inline text-slate-600">•</span>
          <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400/80" /> Vos données sont protégées</span>
        </div>

        {/* Réseaux sociaux */}
        <div className="flex items-center justify-center gap-3 mb-10">
          {[
            { href: 'https://x.com/Padaid_Asso', label: 'X (Twitter)', Icon: XIcon },
            { href: 'https://www.instagram.com/padaid_asso', label: 'Instagram', Icon: Instagram },
            { href: 'https://www.youtube.com/@PADAID', label: 'YouTube', Icon: Youtube },
          ].map(({ href, label, Icon }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noreferrer"
              aria-label={label}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-300 transition-all duration-300 hover:text-emerald-300 hover:border-emerald-400/40 hover:bg-emerald-400/10 hover:scale-110"
            >
              <Icon size={18} />
            </a>
          ))}
        </div>

        {/* Contact */}
        <a
          href="mailto:padaid.asso@gmail.com"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-emerald-300 transition-colors mb-12"
        >
          <Mail className="h-4 w-4" /> padaid.asso@gmail.com
        </a>

        {/* Connexions pendant la maintenance : Admin (prioritaire) + Testeurs */}
        <div className="max-w-sm mx-auto border-t border-white/10 pt-8">
          {!mode ? (
            <div className="space-y-3" data-testid="maintenance-access-choices">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-3">Accès pendant la maintenance</p>
              <button
                onClick={() => { setMode('admin'); setError(''); }}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-400/40 bg-emerald-400/10 px-4 py-3 text-sm font-semibold text-emerald-200 hover:bg-emerald-400/20 transition-colors"
                data-testid="show-admin-login-btn"
              >
                <ShieldCheck className="h-4 w-4" /> Espace administrateur
              </button>
              <button
                onClick={() => { setMode('tester'); setError(''); }}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm font-medium text-slate-300 hover:bg-white/10 transition-colors"
                data-testid="show-tester-login-btn"
              >
                <Wrench className="h-4 w-4" /> Espace testeurs / testrices
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleLogin}
              className="text-left rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-6 space-y-4 shadow-2xl animate-in fade-in slide-in-from-bottom-2"
              data-testid={mode === 'admin' ? 'admin-login-form' : 'tester-login-form'}
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-100">
                {mode === 'admin' ? <ShieldCheck className="h-4 w-4 text-emerald-400" /> : <Wrench className="h-4 w-4 text-sky-400" />}
                {mode === 'admin' ? 'Espace administrateur' : 'Espace testeurs / testrices'}
              </div>
              {error && <div className="text-sm text-red-400" data-testid="maintenance-login-error">{error}</div>}
              {!requires2FA ? (
                <>
                  <div>
                    <label className="block text-xs mb-1.5 text-slate-400">Email</label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={mode === 'admin' ? 'email administrateur' : 'email testeur / testrice'}
                      required
                      autoComplete="email"
                      className="bg-white/5 border-white/10 text-slate-100 placeholder:text-slate-500"
                      data-testid="maintenance-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs mb-1.5 text-slate-400">Mot de passe</label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Votre mot de passe"
                        required
                        autoComplete="current-password"
                        className="bg-white/5 border-white/10 text-slate-100 placeholder:text-slate-500 pr-10"
                        data-testid="maintenance-password-input"
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer select-none">
                    <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="h-3.5 w-3.5 rounded accent-emerald-500" data-testid="maintenance-remember-me" />
                    Me souvenir 30 jours
                  </label>
                </>
              ) : (
                <div>
                  <label className="block text-xs mb-1.5 text-slate-400">Code d'authentification (2FA)</label>                  <Input
                    type="text"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    required
                    maxLength={6}
                    autoComplete="one-time-code"
                    className="text-center text-lg tracking-widest bg-white/5 border-white/10 text-slate-100"
                    data-testid="maintenance-totp-input"
                  />
                  <p className="text-xs text-slate-500 mt-1.5">Entrez le code à 6 chiffres de votre application d'authentification.</p>
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <Button
                  type="submit"
                  disabled={loading}
                  className={`flex-1 text-white gap-2 ${mode === 'admin' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-sky-500 hover:bg-sky-600'}`}
                  data-testid="maintenance-login-submit"
                >
                  {loading ? 'Connexion…' : (<>Se connecter <ArrowRight className="h-4 w-4" /></>)}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="text-slate-400 hover:text-slate-200 hover:bg-white/5"
                  onClick={() => { setMode(null); setError(''); setRequires2FA(false); }}
                >
                  Retour
                </Button>
              </div>
            </form>
          )}
        </div>

        <p className="text-xs text-slate-600 mt-10">© {new Date().getFullYear()} PADAID — Pour un Avenir Durable</p>
        <p className="mt-2 text-sm font-medium italic bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 to-emerald-200" data-testid="maintenance-devise">« Ensemble nous pouvons espérer ! »</p>
      </div>
    </div>
  );
};

export default Maintenance;
